import { Skeleton } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { SurveyService } from "../../api/SurveyService";
import useTranslation from "../../hooks/useTranslation";
import SurveyDispatch from "../../models/surveyDispatch";
import { SurveyRecipient } from "../../types/survey";
import { getFormattedDate } from "../../utils/formatters";
import { validateRecipients } from "../../utils/validation";
import { SurveyRecipientInput } from "../SurveyRecipientInput/SurveyRecipientInput";
import { SurveyRecipientList } from "../SurveyRecipientList/SurveyRecipientList";
import { UploadFile } from "../UploadFile/UploadFile";
import { Button } from "../ui/Button/Button";
import { Card } from "../ui/Card/Card";
import { Modal } from "../ui/Modal/Modal";
import { Tab } from "../ui/Tabs/Tab";
import { Tabs } from "../ui/Tabs/Tabs";
import { Tooltip } from "../ui/Tooltip/Tooltip";
import { Typography } from "../ui/Typography/Typography";
import { IconTypes } from "../ui/icons";
import { Icon } from "../ui/icons/Icon";
import { MailIcon } from "../ui/icons/MailIcon";
import "./SurveyRecipients.scss";

interface SurveyRecipientsProps {
  className?: string;
  onSurveyUpdate?: () => void;
  surveyDispatch?: SurveyDispatch;
  isLoading?: boolean;
  error?: boolean;
}

export const SurveyRecipients = ({
  className,
  onSurveyUpdate,
  surveyDispatch,
  isLoading,
  error,
}: SurveyRecipientsProps) => {
  const { i18n, t } = useTranslation("surveyLaunchPage");
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [recipientList, setRecipientList] = useState<SurveyRecipient[]>([]); // Recipient list to be added to the dispatch
  const [addedRecipients, setAddedRecipients] = useState<SurveyRecipient[]>([]);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [submitError, setSubmitError] = useState<string>("");
  const [submitSuccess, setSubmitSuccess] = useState<string>("");
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const surveyService = new SurveyService();

  useEffect(() => {
    if (surveyDispatch) {
      setAddedRecipients(surveyDispatch.recipients || []);
    } else if (error) {
      setErrorMsg(t("shareLink.errors.fetchError") || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyDispatch, error]);

  const editDispatchMutation = useMutation({
    mutationFn: () => {
      if (!surveyDispatch) {
        throw new Error("Survey dispatch is required");
      }
      return surveyService.editDispatch(surveyDispatch, {
        recipients: recipientList,
      });
    },
  });

  const handleAddRecipients = (newRecipients: SurveyRecipient[]) => {
    // Use validateRecipients to check for duplicates, invalid emails, and invalid phone numbers
    const { duplicates, toBeAdded, invalidEmails, invalidPhones } =
      validateRecipients(recipientList, newRecipients);

    let errorMsg = "";
    if (duplicates.length > 0) {
      errorMsg = t("shareLink.errors.pastedAlreadyInList", {
        value: duplicates.join(", "),
      });
    }
    if (invalidEmails.length > 0) {
      if (errorMsg) errorMsg += "\n"; // Add a newline if there's already an error message
      errorMsg += t("shareLink.errors.invalidEmails", {
        emails: invalidEmails.join(", "),
      });
    }

    if (invalidPhones.length > 0) {
      if (errorMsg) errorMsg += "\n"; // Add a newline if there's already an error message

      if (newRecipients.length === 1) {
        errorMsg += t("shareLink.errors.invalidPhoneManual", {
          phone: invalidPhones
            .map((ip) => `${ip.email} (${ip.phone_number})`)
            .join(", "),
        });
      } else {
        errorMsg += t("shareLink.errors.invalidPhones", {
          phones: invalidPhones
            .map((ip) => `${ip.email} (${ip.phone_number})`)
            .join(", "),
        });
      }
    }

    setErrorMsg(errorMsg);
    setSubmitError("");
    setRecipientList((prev) => {
      const updated = [...prev, ...toBeAdded];
      return updated;
    });

    if (errorMsg) {
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    setErrorMsg("");

    await editDispatchMutation.mutateAsync(undefined, {
      onSuccess: (data) => {
        const invalidRecipients = data.invalid_recipients || [];

        if (invalidRecipients.length) {
          // Process invalid_recipients to separate by error type
          const invalidEmailRecipients = invalidRecipients.filter(
            (r) => r.error_type === "INVALID_EMAIL"
          );
          const invalidPhoneRecipients = invalidRecipients.filter(
            (r) => r.error_type === "INVALID_PHONE_NUMBER"
          );

          let errorMsg = "";
          if (invalidEmailRecipients.length > 0) {
            const invalidEmails = invalidEmailRecipients.map((r) => r.email);
            errorMsg =
              t("shareLink.errors.invalidEmails", {
                emails: invalidEmails.join(", "),
              }) || "";
          } else if (invalidPhoneRecipients.length > 0) {
            errorMsg = errorMsg += t("shareLink.errors.invalidPhones", {
              phones: invalidPhoneRecipients
                .map((ip) => `${ip.email} (${ip.phone_number})`)
                .join(", "),
            });
          }

          // Batch state updates for error case
          setSubmitError(errorMsg);
          setSubmitSuccess("");
        } else {
          // Batch state updates for success case
          setSubmitError("");
          setSubmitSuccess(t("shareLink.submitSuccess") || "");
          setAddedRecipients(data.survey_dispatch?.recipients || []);
        }
        setRecipientList([]);
        onSurveyUpdate && onSurveyUpdate();
      },
      onError: (data) => {
        console.error("SurveyRecipients: handleSave: onError", data);
        // Batch state updates for error case
        setSubmitSuccess("");
        setSubmitError(t("shareLink.errors.submit") || "");
      },
    });
  };

  const handleChangeTab = (newValue: number) => {
    setErrorMsg("");
    setSelectedTabIndex(newValue);
  };

  const renderError = () => {
    return (
      <Typography tagVariant="h5" desktop="body1">
        {errorMsg || t("shareLink.error")}
      </Typography>
    );
  };

  const renderTabs = () => {
    return (
      <>
        {selectedTabIndex === 0 && (
          <>
            <div className="SurveyRecipients__card__panel__text">
              <Typography tagVariant="p" desktop="body1" weight="bold">
                {t("shareLink.upload.title")}
              </Typography>

              <Tooltip
                title={t("shareLink.upload.titleInfo")}
                cardContent={
                  <div className="SurveyRecipients__card__panel__text__tooltip">
                    <Typography desktop="body1" mobile="body2">
                      {t("shareLink.upload.info")}
                    </Typography>
                  </div>
                }
                arrow
                placement="top"
              >
                <span className="SurveyRecipients__card__panel__text__help">
                  {t("shareLink.upload.titleInfo")}{" "}
                  <Icon type={IconTypes.Help} size={20} />
                </span>
              </Tooltip>
            </div>
            <UploadFile
              label={t("shareLink.upload.label")}
              onRecipientsUpload={handleAddRecipients}
              fileUploaded={!!submitSuccess}
            />
          </>
        )}
        {selectedTabIndex === 1 && (
          <>
            <div className="SurveyRecipients__card__panel__text">
              <Typography tagVariant="p" desktop="body1" weight="bold">
                {t("shareLink.type.title")}
              </Typography>
            </div>

            <SurveyRecipientInput
              recipientList={recipientList || []}
              handleAddRecipients={handleAddRecipients}
              setError={setErrorMsg}
              submitSuccess={submitSuccess}
              setSubmitSuccess={setSubmitSuccess}
            />
          </>
        )}
      </>
    );
  };

  const classes = classNames("SurveyRecipients", className);
  return (
    <div>
      <div className={classes}>
        <Card className="SurveyRecipients__card">
          <>
            {isLoading && (
              <Skeleton variant="rounded" width={"100%"} height={"2rem"} />
            )}
            {error && renderError()}
            {!isLoading && !error && (
              <>
                <Tabs
                  className="SurveyRecipients__card__tabs"
                  selectedTabIndex={selectedTabIndex}
                  label="Survey-invite"
                >
                  <Tab
                    key={"sendOutSurveyTab0"}
                    selected={selectedTabIndex === 0}
                    label={t("shareLink.upload.label")}
                    index={0}
                    onClick={handleChangeTab}
                    withIndicator
                    data-testid="upload-tab"
                  />
                  <Tab
                    key={"sendOutSurveyTab1"}
                    selected={selectedTabIndex === 1}
                    label={t("shareLink.type.label")}
                    index={1}
                    onClick={handleChangeTab}
                    withIndicator
                    data-testid="manual-tab"
                  />
                </Tabs>
                <div className="SurveyRecipients__card__panel">
                  {renderTabs()}
                  {errorMsg && (
                    <Typography
                      desktop="caption"
                      color="error"
                      className="SurveyRecipients__card__panel__error"
                      data-testid="error-message"
                    >
                      {errorMsg}
                    </Typography>
                  )}
                </div>
                {addedRecipients && addedRecipients.length > 0 && (
                  <Card
                    variant="outlined"
                    className="SurveyRecipients__card__status"
                    onClick={() => setModalOpen(true)}
                  >
                    <div className="SurveyRecipients__card__status__title">
                      <MailIcon className="SurveyRecipients__card__status__title__icon" />
                      <Typography tagVariant="p" desktop="body2">
                        {t("shareLink.addedEmails", {
                          count: addedRecipients?.length,
                        })}
                      </Typography>
                    </div>
                  </Card>
                )}
                {recipientList.length > 0 && (
                  <>
                    <SurveyRecipientList
                      recipientList={recipientList || []}
                      setRecipientList={setRecipientList}
                    />
                    <div className="SurveyRecipients__card__action">
                      <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={editDispatchMutation.isLoading}
                        loading={editDispatchMutation.isLoading}
                      >
                        {t("shareLink.save")}
                      </Button>
                      {recipientList.length > 0 &&
                        !submitSuccess &&
                        !submitError &&
                        !surveyDispatch?.isActive() && (
                          <Typography
                            tagVariant="p"
                            desktop="caption"
                            mobile="caption"
                          >
                            {t(
                              surveyDispatch?.launchDate
                                ? "shareLink.employeeNr"
                                : "shareLink.employeeNrNoDate",
                              {
                                count: recipientList.length,
                                date: surveyDispatch?.launchDate
                                  ? getFormattedDate(
                                      surveyDispatch.launchDate,
                                      t,
                                      i18n.language
                                    )
                                  : "",
                              }
                            )}
                          </Typography>
                        )}
                    </div>
                  </>
                )}
                {submitSuccess && (
                  <Typography
                    tagVariant="p"
                    desktop="caption"
                    className="SurveyRecipients__text"
                  >
                    {submitSuccess}
                  </Typography>
                )}
                {submitError && (
                  <Typography
                    tagVariant="p"
                    desktop="caption"
                    color="error"
                    className="SurveyRecipients__text"
                  >
                    {submitError}
                  </Typography>
                )}
              </>
            )}
          </>
        </Card>
      </div>

      <Modal
        className="SurveyRecipients__modal"
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={
          <Typography tagVariant="h3" desktop="h3" weight="bold">
            {t("shareLink.recipients")}
          </Typography>
        }
        aria-labelledby="recipients-modal-title"
        aria-describedby="recipients-modal-description"
      >
        <div>
          <div id="recipients-modal-description" className="sr-only">
            {t("shareLink.recipientsModalDescription") ||
              "List of all recipients added to the survey"}
          </div>
          <ul
            className="SurveyRecipients__modal__list"
            aria-label={t("shareLink.recipients")}
          >
            {addedRecipients &&
              addedRecipients.sort().map((recipient) => (
                <li
                  className="SurveyRecipients__modal__list__item"
                  key={recipient.email}
                  tabIndex={0}
                >
                  {`${recipient.email} ${
                    recipient.phone_number ? `(${recipient.phone_number})` : ""
                  }`}
                </li>
              ))}
          </ul>
        </div>
      </Modal>
    </div>
  );
};
